import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';

// ──────────────────────────────────────────────────────────────────────
// CloseCaseModal — confirmation modal for closing a Concierge case.
// Wired to POST /api/admin/concierge/cases/{id}/close via the parent's
// onConfirm handler. Backend requires reason + resolution_note.
// ──────────────────────────────────────────────────────────────────────

const CLOSE_REASONS = [
  { value: 'completed', label: 'Completed — customer booked and event is set' },
  { value: 'customer_not_ready', label: 'Customer not ready right now' },
  { value: 'no_response', label: 'No response from customer' },
  { value: 'out_of_scope', label: 'Out of scope for Concierge' },
  { value: 'duplicate', label: 'Duplicate of another case' },
  { value: 'other', label: 'Other' },
];

export default function CloseCaseModal({ open, onClose, onConfirm, saving }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const canSubmit = reason && note.trim().length > 0 && !saving;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onConfirm({ reason, resolution_note: note.trim() });
    // Reset local state after successful close (parent closes the modal)
    setReason('');
    setNote('');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent data-testid="close-case-modal">
        <DialogHeader>
          <DialogTitle>Close this case</DialogTitle>
          <DialogDescription>
            Tell the team why you&apos;re closing this case. This is written to the case history and
            not shown to the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-[12px] font-medium">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="close-case-reason-trigger" className="mt-1.5">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {CLOSE_REASONS.map((r) => (
                  <SelectItem
                    key={r.value}
                    value={r.value}
                    data-testid={`close-case-reason-${r.value}`}
                  >
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[12px] font-medium">Resolution note</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Short internal summary of how this case wrapped up."
              className="mt-1.5"
              data-testid="close-case-note-textarea"
            />
            <p className="text-[10.5px] text-gray-400 mt-1">{note.length} / 2000</p>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            data-testid="close-case-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!canSubmit}
            data-testid="close-case-confirm-btn"
          >
            {saving ? 'Closing…' : 'Close case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
